import React, { useState } from 'react';
import { CONFIG } from '../data/config';
import { FlameIcon, GlobeIcon, ChevronDownIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { LANGUAGES, Language } from '../data/translations';
import { motion, AnimatePresence } from 'framer-motion';
interface HeaderProps {
  onStartOrder: () => void;
  onLookupOrder?: () => void;
}
const HERO_IMAGE = 'https://cdn.magicpatterns.com/uploads/mupTnAiYzPJPXgc2qFEfRm/ChatGPT_Image_15_mar_2026,_13_53_22.png';
export function Header({
  onStartOrder,
  onLookupOrder
}: HeaderProps) {
  const {
    language,
    setLanguage,
    t
  } = useLanguage();
  const [isLangOpen, setIsLangOpen] = useState(false);
  const currentLang = LANGUAGES.find(l => l.code === language) || LANGUAGES[0];
  return <header className="relative mb-0 overflow-hidden" style={{
    minHeight: 280
  }} data-id="element-1224">
      {/* Background image */}
      <div className="absolute inset-0 will-change-transform" data-id="element-1225">
        <img src={HERO_IMAGE} alt="" loading="eager" decoding="async" className="w-full h-full object-cover object-center" style={{
        willChange: 'auto'
      }} data-id="element-1226" />
        <div className="absolute inset-0 bg-black/50" data-id="element-1227" />
        <div className="absolute bottom-0 left-0 right-0 h-40" style={{
        background: 'linear-gradient(to top, #0D0D0F 0%, #0D0D0F 10%, transparent 100%)'
      }} data-id="element-1228" />
      </div>

      {/* Language Selector */}
      <div className="absolute top-4 right-4 z-20" data-id="element-1229">
        <button onClick={() => setIsLangOpen(!isLangOpen)} className="flex items-center gap-2 bg-carbon/80 backdrop-blur-md border border-gray-800 rounded-full px-3 py-1.5 text-white text-sm shadow-lg hover:bg-carbon transition-colors" data-id="element-1230">
          <span className="text-base leading-none" data-id="element-1231">{currentLang.flag}</span>
          <span className="font-medium uppercase" data-id="element-1232">{currentLang.code}</span>
          <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${isLangOpen ? 'rotate-180' : ''}`} data-id="element-1233" />
        </button>

        <AnimatePresence data-id="element-1234">
          {isLangOpen && <motion.div initial={{
          opacity: 0,
          y: -10,
          scale: 0.95
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: -10,
          scale: 0.95
        }} transition={{
          duration: 0.15
        }} className="absolute top-full right-0 mt-2 w-40 bg-surface border border-gray-800 rounded-2xl shadow-2xl overflow-hidden py-1 z-30" data-id="element-1235">
              {LANGUAGES.map(lang => <button key={lang.code} onClick={() => {
            setLanguage(lang.code as Language);
            setIsLangOpen(false);
          }} className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors ${language === lang.code ? 'bg-surface-light text-white font-semibold' : 'text-gray-300 hover:bg-surface-light/50 hover:text-white'}`} data-id="element-1236">
                  <span className="text-lg leading-none" data-id="element-1237">{lang.flag}</span>
                  <span data-id="element-1238">{lang.nativeName}</span>
                </button>)}
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Table Banner */}
      {(() => {
      const table = new URLSearchParams(window.location.search).get('table');
      return table ? <div className="absolute top-4 left-4 z-20 bg-dorado/90 backdrop-blur-md rounded-full px-4 py-1.5 flex items-center gap-2 shadow-lg" data-id="element-1239">
            <span className="text-lg leading-none" data-id="element-1240">🍽️</span>
            <span className="text-carbon font-bold text-sm" data-id="element-1241">Mesa {table}</span>
          </div> : null;
    })()}

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center text-center px-6 pt-10 pb-14" data-id="element-1242">
        <div className="w-24 h-24 rounded-2xl shadow-2xl flex items-center justify-center mb-4 overflow-hidden border border-[#2A2A2F] bg-[#16161A]" data-id="element-1243">
          <img src={CONFIG.LOGO_URL} alt={CONFIG.APP_NAME} loading="eager" decoding="async" width={96} height={96} className="w-full h-full object-contain p-1" data-id="element-1244" />
        </div>

        <h1 className="text-4xl font-heading font-bold text-white mb-1 drop-shadow-lg tracking-tight" data-id="element-1245">
          <span className="noiza-gradient-text" data-id="element-1246">{CONFIG.APP_NAME}</span>
        </h1>

        <p className="text-[#A1A1AA] text-sm mb-6 flex items-center justify-center gap-1.5 font-medium tracking-wide drop-shadow" data-id="element-1247">
          {CONFIG.APP_TAGLINE}
        </p>

        <button onClick={onStartOrder} className="noiza-gradient text-[#0D0D0F] font-bold py-3.5 px-10 rounded-full shadow-xl shadow-[#00F5C9]/20 active:scale-95 transition-transform flex items-center gap-2 w-full max-w-xs justify-center min-h-[48px]" data-id="element-1248">
          {t('orderNow')}
        </button>

        {onLookupOrder && <button onClick={onLookupOrder} className="mt-5 text-sm text-gray-400 hover:text-white underline underline-offset-4 decoration-gray-600 hover:decoration-white transition-all" data-id="element-1249">
            ¿Cuál es el estado de mi pedido?
          </button>}
      </div>
    </header>;
}